import axios from "axios";
import Config from "../_config/config";

export interface AiPromptConfigData {
  id: number;
  studyResourceId: number;
  name: string;
  systemPrompt: string;
  instructionsForStudent: string | null;
  maxTurns: number;
  maxTokensPerResponse: number;
  temperature: string;
  model: string;
  guardrails: any;
  requireReflection: number;
  reflectionPrompt: string | null;
  activeFlag: number;
  createdByUserId: number | null;
  // Template linking fields
  sourceTemplateId: number | null;
  templateVersionApplied: number | null;
  isSyncedWithTemplate: number;
  createdAt: string;
  updatedAt: string;
}

export interface AiPromptConfigTemplate {
  id: number;
  name: string;
  description: string | null;
  systemPrompt: string;
  instructionsForStudent: string | null;
  maxTurns: number;
  maxTokensPerResponse: number;
  temperature: string;
  model: string;
  guardrails: any;
  requireReflection: number;
  reflectionPrompt: string | null;
  createdByUserId: number;
  isShared: number;
  usageCount: number;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkspaceSession {
  id: number;
  userId: number;
  aiPromptConfigId: number;
  studyResourceId: number;
  status: "ACTIVE" | "COMPLETED" | "SUBMITTED" | "EXPIRED";
  conversationHistory: ConversationMessage[];
  turnCount: number;
  reflectionText: string | null;
  submissionId: number | null;
  totalTokensUsed: number;
  startedAt: string;
  lastInteractionAt: string;
  completedAt: string | null;
  submittedAt: string | null;
}

export interface ConversationMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  tokenUsage?: { inputTokens: number; outputTokens: number };
  processingTimeMs?: number;
}

export interface PromptConfigSafe {
  name: string;
  instructionsForStudent: string | null;
  maxTurns: number;
  requireReflection: number;
  reflectionPrompt: string | null;
}

class AiWorkspaceService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/ai-workspace`;
  }

  private getAuthHeaders(): any {
    const headers: any = { "Content-Type": "application/json" };
    const currentUser = localStorage.getItem("currentUser");
    if (currentUser && currentUser !== "undefined") {
      try {
        const user = JSON.parse(currentUser);
        if (user.token) {
          headers["Authorization"] = `Bearer ${user.token}`;
        }
      } catch (error) {
        console.error("Failed to parse currentUser:", error);
      }
    }
    return headers;
  }

  // ========== Faculty: Prompt Config ==========

  async createConfig(data: {
    studyResourceId: number;
    name: string;
    systemPrompt: string;
    instructionsForStudent?: string;
    maxTurns?: number;
    maxTokensPerResponse?: number;
    temperature?: number;
    model?: string;
    guardrails?: any;
    requireReflection?: boolean;
    reflectionPrompt?: string;
  }): Promise<{ success: boolean; data?: AiPromptConfigData; error?: string; message?: string }> {
    try {
      const response = await axios.post<any>(`${this.baseUrl}/config`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to create config",
      };
    }
  }

  async batchCheckConfigStatus(
    resourceIds: number[]
  ): Promise<{ success: boolean; data?: number[] }> {
    try {
      const response = await axios.post<any>(
        `${this.baseUrl}/config/batch-status`,
        { resourceIds },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return { success: false, data: [] };
    }
  }

  async getConfigByResource(
    resourceId: number
  ): Promise<{ success: boolean; data?: AiPromptConfigData; error?: string }> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/config/resource/${resourceId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Not found",
      };
    }
  }

  async updateConfig(
    configId: number,
    data: Partial<AiPromptConfigData>
  ): Promise<{ success: boolean; data?: AiPromptConfigData; error?: string }> {
    try {
      const response = await axios.put<any>(
        `${this.baseUrl}/config/${configId}`,
        data,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to update",
      };
    }
  }

  async testConfig(
    configId: number,
    message: string
  ): Promise<{
    success: boolean;
    data?: { response: string; tokenUsage: any; processingTimeMs: number };
    error?: string;
  }> {
    try {
      const response = await axios.post<any>(
        `${this.baseUrl}/config/${configId}/test`,
        { message },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to test prompt",
      };
    }
  }

  async analyzeConfig(configId: number): Promise<{
    success: boolean;
    data?: {
      issues: Array<{
        id: string;
        severity: "error" | "warning" | "info";
        message: string;
        suggestion?: string;
      }>;
      overallAssessment: string;
      tokenUsage: { inputTokens: number; outputTokens: number };
      processingTimeMs: number;
    };
    error?: string;
  }> {
    try {
      const response = await axios.post<any>(
        `${this.baseUrl}/config/${configId}/analyze`,
        {},
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to analyze config",
      };
    }
  }

  // ========== Faculty: Config Templates ==========

  async getTemplates(): Promise<{
    success: boolean;
    data?: {
      own: AiPromptConfigTemplate[];
      shared: AiPromptConfigTemplate[];
      total: number;
    };
    error?: string;
  }> {
    try {
      const response = await axios.get(`${this.baseUrl}/templates`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch templates",
      };
    }
  }

  async getTemplateById(
    templateId: number
  ): Promise<{ success: boolean; data?: AiPromptConfigTemplate; error?: string }> {
    try {
      const response = await axios.get(`${this.baseUrl}/templates/${templateId}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch template",
      };
    }
  }

  async createTemplate(data: {
    name: string;
    description?: string;
    systemPrompt: string;
    instructionsForStudent?: string;
    maxTurns?: number;
    maxTokensPerResponse?: number;
    temperature?: number;
    model?: string;
    guardrails?: any;
    requireReflection?: boolean;
    reflectionPrompt?: string;
    isShared?: boolean;
    fromConfigId?: number;
  }): Promise<{ success: boolean; data?: AiPromptConfigTemplate; error?: string }> {
    try {
      const response = await axios.post<any>(`${this.baseUrl}/templates`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to create template",
      };
    }
  }

  async updateTemplate(
    templateId: number,
    data: Partial<AiPromptConfigTemplate>
  ): Promise<{ success: boolean; data?: AiPromptConfigTemplate; error?: string }> {
    try {
      const response = await axios.put<any>(`${this.baseUrl}/templates/${templateId}`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to update template",
      };
    }
  }

  async deleteTemplate(
    templateId: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await axios.delete<any>(`${this.baseUrl}/templates/${templateId}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to delete template",
      };
    }
  }

  async createConfigFromTemplate(
    templateId: number,
    studyResourceId: number,
    name?: string
  ): Promise<{ success: boolean; data?: AiPromptConfigData; error?: string }> {
    try {
      const response = await axios.post<any>(
        `${this.baseUrl}/config/from-template`,
        { templateId, studyResourceId, name },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to create config from template",
      };
    }
  }

  async getLinkedConfigs(
    templateId: number
  ): Promise<{
    success: boolean;
    data?: {
      template: { id: number; name: string; version: number };
      linkedConfigs: Array<{
        id: number;
        name: string;
        studyResourceId: number;
        templateVersionApplied: number | null;
        isSyncedWithTemplate: number;
        needsUpdate: boolean;
      }>;
      totalLinked: number;
      needingUpdate: number;
    };
    error?: string;
  }> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/templates/${templateId}/linked-configs`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to get linked configs",
      };
    }
  }

  async syncAllFromTemplate(
    templateId: number
  ): Promise<{
    success: boolean;
    data?: { syncedCount: number; totalLinked: number; templateVersion: number };
    error?: string;
  }> {
    try {
      const response = await axios.post<any>(
        `${this.baseUrl}/templates/${templateId}/sync-all`,
        {},
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to sync from template",
      };
    }
  }

  async syncConfigFromTemplate(
    configId: number
  ): Promise<{ success: boolean; data?: AiPromptConfigData; error?: string }> {
    try {
      const response = await axios.post<any>(
        `${this.baseUrl}/config/${configId}/sync-from-template`,
        {},
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to sync from template",
      };
    }
  }

  async unlinkConfigFromTemplate(
    configId: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await axios.post<any>(
        `${this.baseUrl}/config/${configId}/unlink-template`,
        {},
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to unlink from template",
      };
    }
  }

  // ========== Student: Workspace Sessions ==========

  async startSession(
    studyResourceId: number
  ): Promise<{
    success: boolean;
    data?: { session: WorkspaceSession; promptConfig: PromptConfigSafe };
    error?: string;
    message?: string;
  }> {
    try {
      const response = await axios.post<any>(
        `${this.baseUrl}/session/start`,
        { studyResourceId },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to start session",
      };
    }
  }

  async getSession(
    sessionId: number
  ): Promise<{
    success: boolean;
    data?: {
      session: WorkspaceSession;
      promptConfig: PromptConfigSafe;
      turnsRemaining: number;
    };
    error?: string;
  }> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/session/${sessionId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to get session",
      };
    }
  }

  async getSessionByResource(
    resourceId: number
  ): Promise<{
    success: boolean;
    data?: {
      session: WorkspaceSession;
      promptConfig: PromptConfigSafe;
      turnsRemaining: number;
    };
    error?: string;
  }> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/session/resource/${resourceId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "No session found",
      };
    }
  }

  async sendMessage(
    sessionId: number,
    message: string
  ): Promise<{
    success: boolean;
    data?: {
      response: string;
      turnCount: number;
      maxTurns: number;
      turnsRemaining: number;
      sessionStatus: string;
      tokenUsage: any;
      processingTimeMs: number;
      ragInfo?: {
        contextsRetrieved: number;
        ragEnabled: boolean;
        sources: Array<{ source: string; page?: number; relevanceScore: number }>;
      };
    };
    error?: string;
  }> {
    try {
      const response = await axios.post<any>(
        `${this.baseUrl}/session/${sessionId}/message`,
        { message },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to send message",
      };
    }
  }

  async saveReflection(
    sessionId: number,
    reflectionText: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await axios.patch<any>(
        `${this.baseUrl}/session/${sessionId}/reflection`,
        { reflectionText },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to save reflection",
      };
    }
  }

  async submitSession(
    sessionId: number,
    reflectionText?: string
  ): Promise<{
    success: boolean;
    data?: { submissionId: number; sessionId: number };
    error?: string;
    message?: string;
  }> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/session/${sessionId}/submit`,
        { reflectionText },
        { headers: this.getAuthHeaders() }
      );
      return response.data as any;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to submit",
      };
    }
  }

  async deleteSession(
    sessionId: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await axios.delete(
        `${this.baseUrl}/session/${sessionId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data as any;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to delete session",
      };
    }
  }

  // ========== Global AI Config (tenant-level settings by type) ==========

  async getGlobalConfig(type: "doubt" | "generic" | "resource-default" | "atlas-counselor"): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await axios.get<any>(`${this.baseUrl}/config/global/${type}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch global config",
      };
    }
  }

  async updateGlobalConfig(type: "doubt" | "generic" | "resource-default" | "atlas-counselor", data: {
    name?: string;
    systemPrompt: string;
    guardrails?: any;
    instructionsForStudent?: string;
    maxTurns?: number;
    maxTokensPerResponse?: number;
    temperature?: number;
    model?: string;
    requireReflection?: boolean;
    reflectionPrompt?: string;
    knowledgeBaseLessonPlanId?: number | null;
  }): Promise<{ success: boolean; id?: number; error?: string; message?: string }> {
    try {
      const response = await axios.put<any>(`${this.baseUrl}/config/global/${type}`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to save global config",
      };
    }
  }
}

export default new AiWorkspaceService();
